// WHT Control ledger (requirement 12). One record per approved invoice
// snapshot, lazily created the first time it's needed (either by a receipt
// allocation recognizing a deduction, or by the WHT Control tab being
// opened) rather than written at Stage 1 approval time — this keeps Stage 1's
// already-tested approval transactions untouched. "Expected" fields are read
// straight off the snapshot (never duplicated); only the fields that need
// their own lifecycle (certificate, classification, claimed amount, status)
// live on this record.
import {
  collection, doc, getDoc, getDocs, query, serverTimestamp, setDoc, where
} from "firebase/firestore";
import { auth, db } from "../../firebase";
import { activeSnapshotDocs } from "./voidedInvoice";

const CONTROL_RECORDS = "wht_control_records";
const SNAPSHOTS = "approved_invoice_snapshots";

export const WHT_CONTROL_STATUSES = Object.freeze({
  EXPECTED: "EXPECTED",
  DEDUCTED: "DEDUCTED",
  CERTIFICATE_PENDING: "CERTIFICATE_PENDING",
  CERTIFICATE_RECEIVED: "CERTIFICATE_RECEIVED",
  CLAIMED: "CLAIMED",
  ADJUSTED: "ADJUSTED",
  CLOSED: "CLOSED",
  DISPUTED: "DISPUTED"
});

// READ-ONLY half — must be called (via Promise.all across every affected
// snapshot) before ANY transaction.set()/update() in the caller's
// transaction, per Firestore's all-reads-before-any-write rule.
export async function readWhtControlLookup(transaction, snapshotId) {
  const snapshotRef = doc(db, SNAPSHOTS, snapshotId);
  const controlRef = doc(db, CONTROL_RECORDS, snapshotId);
  const [snapshotDoc, controlDoc] = await Promise.all([
    transaction.get(snapshotRef), transaction.get(controlRef)
  ]);
  if (!snapshotDoc.exists()) throw new Error(`Approved invoice snapshot ${snapshotId} not found.`);
  return {
    snapshotId,
    controlRef,
    snapshotData: snapshotDoc.data(),
    existingControlData: controlDoc.exists() ? controlDoc.data() : null
  };
}

// WRITE-ONLY half — safe to call any time after every read in the
// transaction is done. delta is signed (positive when recognizing a
// deduction, negative when a reversal unwinds one).
export function applyWhtRecognition(transaction, lookup, delta) {
  const { controlRef, snapshotData, existingControlData } = lookup;
  const baseline = existingControlData || baselineFromSnapshot(snapshotData);
  const actualWhtDeducted = Math.round(Number(baseline.actualWhtDeducted || 0) + Number(delta || 0));
  const status = !existingControlData
    ? (actualWhtDeducted > 0 ? WHT_CONTROL_STATUSES.DEDUCTED : WHT_CONTROL_STATUSES.EXPECTED)
    : autoStatusTransition(existingControlData.status, actualWhtDeducted);

  transaction.set(controlRef, {
    ...baseline,
    actualWhtDeducted,
    amountRemaining: Math.round(actualWhtDeducted - Number(baseline.amountClaimed || 0)),
    status,
    updatedBy: auth.currentUser?.uid || null,
    updatedByEmail: auth.currentUser?.email || null,
    updatedAt: serverTimestamp()
  }, { merge: true });
}

// Only the two "automatic" states (EXPECTED/DEDUCTED) are ever changed by a
// receipt allocation — once finance manually moves a record into a
// certificate/claim/close/dispute state, further recognitions update
// actualWhtDeducted but leave that manual status alone for finance to review.
function autoStatusTransition(currentStatus, actualWhtDeducted) {
  if (currentStatus !== WHT_CONTROL_STATUSES.EXPECTED && currentStatus !== WHT_CONTROL_STATUSES.DEDUCTED) {
    return currentStatus;
  }
  return actualWhtDeducted > 0 ? WHT_CONTROL_STATUSES.DEDUCTED : WHT_CONTROL_STATUSES.EXPECTED;
}

function baselineFromSnapshot(snapshotData) {
  return {
    clientCode: snapshotData.clientCode,
    invoiceNumber: snapshotData.invoiceNumber,
    invoiceDate: snapshotData.invoiceDate,
    whtModel: snapshotData.whtModelUsed,
    whtBase: snapshotData.totals?.serviceChargeSubtotal ?? snapshotData.totals?.grossAmount ?? 0,
    expectedWht: snapshotData.totals?.whtAmount || 0,
    actualWhtDeducted: 0,
    certificateNumber: null,
    certificateDate: null,
    taxYear: null,
    classification: "ADJUSTABLE",
    amountClaimed: 0,
    amountRemaining: 0,
    status: WHT_CONTROL_STATUSES.EXPECTED,
    createdAt: serverTimestamp()
  };
}

// Non-transactional convenience used by the WHT Control tab: one row per
// approved invoice for a client, joining the snapshot's expected figures with
// whatever control record exists (or the EXPECTED baseline if none yet).
export async function listWhtControlRows(clientCode) {
  const snapshotsQuery = query(collection(db, SNAPSHOTS), where("clientCode", "==", clientCode));
  const snapshotsResult = await getDocs(snapshotsQuery);
  const rows = await Promise.all(activeSnapshotDocs(snapshotsResult).map(async (snapshotDoc) => {
    const controlDoc = await getDoc(doc(db, CONTROL_RECORDS, snapshotDoc.id));
    const baseline = baselineFromSnapshot({ id: snapshotDoc.id, ...snapshotDoc.data() });
    return { id: snapshotDoc.id, ...(controlDoc.exists() ? controlDoc.data() : baseline) };
  }));
  return rows;
}

// Finance-entered fields only (certificate/classification/claimed/status) —
// never touches actualWhtDeducted, which only receipt recognition can change.
// Creates the control record from the snapshot baseline if this is the first
// edit finance makes before any receipt has recognized a deduction.
export async function updateWhtControlRecord(snapshotId, updates) {
  const controlRef = doc(db, CONTROL_RECORDS, snapshotId);
  const existingDoc = await getDoc(controlRef);
  let baseline = existingDoc.exists() ? existingDoc.data() : null;
  if (!baseline) {
    const snapshotDoc = await getDoc(doc(db, SNAPSHOTS, snapshotId));
    if (!snapshotDoc.exists()) throw new Error(`Approved invoice snapshot ${snapshotId} not found.`);
    baseline = baselineFromSnapshot(snapshotDoc.data());
  }
  const amountClaimed = updates.amountClaimed != null ? Number(updates.amountClaimed) : Number(baseline.amountClaimed || 0);
  const actualWhtDeducted = Number(baseline.actualWhtDeducted || 0);
  await setDoc(controlRef, {
    ...baseline,
    ...updates,
    amountClaimed,
    amountRemaining: Math.round(actualWhtDeducted - amountClaimed),
    updatedBy: auth.currentUser?.uid || null,
    updatedByEmail: auth.currentUser?.email || null,
    updatedAt: serverTimestamp()
  }, { merge: true });
}

// Backwards-compatible aliases used by receiptAllocationService.js.
export const ensureWhtControlRecordInTransaction = readWhtControlLookup;
export const recognizeWhtDeductionInTransaction = applyWhtRecognition;
